import { useEffect, useRef, useState } from "react";
import { termsReady } from "../lib/termsOfUse";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Bell, BarChart3, Compass, Gift, HeartPulse, UserPlus, UserRound, Users } from "lucide-react";
import { TopBar, DestinationRow, CoachRibbon, TextLink, MoreSection, SecondaryAction } from "./kit";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { useAuth } from "../context/AuthContext";
import { useLearnCoachNewLook } from "./LearnChrome";
import { homePathForRole, homeLabelForRole } from "../lib/homePath";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import SettingsDetailsCard from "./SettingsDetailsCard";
import SettingsDetailsSheet from "./SettingsDetailsSheet";
import SettingsSheet from "./SettingsSheet";
import SettingsMoreCard from "./SettingsMoreCard";
import UsageFirstTouch from "./UsageFirstTouch";
import { useSettingsSignOut, SignOutButton, SignOutEverywhere, SignOutError, OUTLINE_ACTION_STYLE } from "./SettingsSignOut";
import CoachAccessCardNew from "./CoachAccessCardNew";
import BillingCard from "./BillingCard";
import InviteFriendCard from "./InviteFriendCard";
import CoachProfileCard from "./CoachProfileCard";
import PasskeysCard from "./PasskeysCard";
import HealthInfoCard from "./HealthInfoCard";
import NotificationsCard from "./NotificationsCard";
import SendFeedbackCard from "./SendFeedbackCard";
import UsageDisclosureCard from "./UsageDisclosureCard";
import DeleteAccountCard from "./DeleteAccountCard";
import AddPlayerCard from "./AddPlayerCard";
import { SectionCard } from "./identityForm";
import PlayerProfile from "../pages/PlayerProfile";
import api from "../lib/api";
import { keepCentred } from "../lib/keepCentred";
import { cricketProfileSummary, SUMMARY_NONE } from "../lib/settingsSummary";
import { BUILD_STAMP } from "../buildInfo";
import { newUi } from "../lib/featureFlags";
import { AVAILABLE_WITH_PLAN, useUnpaidPaywall } from "../lib/unpaidPaywall";
import { tourReplayHref } from "../lib/tour";

/**
 * Issue #1135 (D219, D221 O11, D243, D254 Q5) — `/settings`, opening simple.
 * The face is what a player or coach comes here to do: see their details and
 * edit them in one sheet, sign out, read the privacy notice. Everything rarely
 * used sits behind `More` (`kit/MoreSection`, remembered per person under
 * `nw_more:settings:<userId>`, D223 Q2). The drawing is the round-10 boards
 * SettingsPlayer and SettingsCoach (overlay `design-round-10`).
 *
 * Behind `newUi("settings")`, player and coach (`pages/Settings.jsx`'s own
 * early return; an admin keeps the shipped page, D186.9).
 *
 * THE FACE: head (`Settings` / `Your account`), the details card, `Sign out`,
 * `More`, `Privacy notice`, and the build line under it (text, not a choice).
 *
 * MORE, PLAYER: Passkeys, Your coach, the Player Profile fold, Health,
 * Notifications, Send feedback, Take the tour (#1514, D407a, with
 * `newUi("tour")`), What we measure, Sign out everywhere, Delete account.
 * MORE, COACH: Add a player, Your coaching profile, Passkeys, Notifications,
 * Send feedback, Take the tour (#1514, as the player's), What we measure, Sign
 * out everywhere, Delete account (a real coach only). What we measure is a
 * closed fold and the last one before Sign out everywhere for both (D400, which
 * reverses #1135 R1/F5; Take the tour sits between, D407b). The undrawn items are inside More as their shipped folds (D254
 * Q5). The coach's Add a player and coaching profile keep their shipped
 * bodies; the card header is the board's (`SettingsMoreCard`).
 *
 * THE TWO SHEETS (`ui/sheet`, `role="dialog"`, judged on their own, Q-C2): Edit
 * your details (name, phone, photo, one Save) and, for a coach, Your coaching
 * profile (the shipped `CoachProfileCard`).
 *
 * The invite Accept link (`/settings?open=more`, R6) opens More for that visit
 * without writing the person's memory and scrolls Your coach into view.
 *
 * `AccessibilityMenu compact` (D25): several of the folds keep their shipped
 * Tailwind `text-sm`/`text-xs` content, which is `rem`-based and moves with the
 * text size, so the control stays (see the PR bodies of #975 and #976).
 * No `TextSizeCard` is imported (D86, M-16).
 */
export default function SettingsScreen() {
  const { setActive: setChromeless } = useChromelessOverride();
  const { user } = useAuth();
  const isCoach = user?.role === "coach";
  // D476 F6: behind the paywall, the cards whose data the paywall withholds say so in one plain line.
  const unpaid = useUnpaidPaywall();
  const gated = (icon, title, testid) => <SettingsMoreCard icon={icon} title={title} subtitle={AVAILABLE_WITH_PLAN} data-testid={testid} />;
  // D216 and #1121: the bar is white for both roles; the ribbon under it is
  // the coach's own for a coach (D221 O1).
  const surface = "white";
  // F4: the coach ribbon appears only where its destinations draw it too, on
  // the same configuration as the portal and /learn (both `coach-portal-empty`
  // and `squad` on).
  const coachRibbon = useLearnCoachNewLook();

  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // R6: the invite Accept link lands here as `/settings?open=more`. It opens
  // More for this visit (no memory written), scrolls Your coach into view and
  // drops the query, so a reload shows the person's own choice again.
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const openMore = params.get("open") === "more";
  // #1544: the coach-setup-off invite fallback adds `#settings-add-player`, so
  // that visit scrolls to the Add a player card instead of Your coach.
  const location = useLocation();
  const { hash } = location;
  const scrollTarget = hash === "#settings-add-player" ? "settings-add-player" : "settings-coach-access-card";
  const stopCentring = useRef(null);
  useEffect(() => () => stopCentring.current?.(), []);
  useEffect(() => {
    if (!openMore) return undefined;
    stopCentring.current?.();
    // #1655: this screen owns the final position (it marks its root `data-scroll-owner`, so
    // `ScrollToTop` leaves an in-app move alone) and keeps the card centred while the layout settles.
    stopCentring.current = keepCentred(() => document.querySelector(`[data-testid="${scrollTarget}"]`));
    // Drop the query, keep the hash: `ScrollToTop` is keyed on the hash, so
    // losing it would re-run that effect and scroll the page back to the top.
    navigate({ pathname: location.pathname, search: "", hash }, { replace: true });
    // Not this effect's cleanup: dropping the query below flips `openMore` and must not end the
    // centring; it ends on an outside scroll, the reader's input, the time limit, a new visit or leaving.
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMore]);

  // The SAVED club and team: line two of the details card and the Player Profile
  // fold's header. Read once here and replaced by the server's answer when the
  // fold saves (`onSaved`), never by what is being typed (F4).
  const [playerRecord, setPlayerRecord] = useState(null);
  useEffect(() => {
    if (isCoach || user?.role !== "player") return undefined;
    let live = true;
    api
      .get("/players/me")
      .then((r) => { if (live) setPlayerRecord({ club: r.data?.club, school: r.data?.school }); })
      .catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [coachProfileOpen, setCoachProfileOpen] = useState(false);
  const signOut = useSettingsSignOut();

  return (
    <div data-testid="settings-page" data-scroll-owner={openMore ? "" : undefined}>
      {/* The convention every rebuilt screen carries (issue #823's own
          pattern, e.g. `signup-new-screen`, `privacy-notice-new-screen`) —
          an inert marker the responsive/spacing/text-size harnesses scope
          their own measurements to, so a shared component rendered
          elsewhere (a coach's shipped page, say) is never mistaken for
          this screen. `responsive.spec.js`'s own `EXPECTED_NEW_SCREEN_ROUTES`
          names `/settings` + this testid, player and (since #976) coach
          personas. */}
      <span data-testid="settings-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />
      <TopBar
        variant="white"
        homeHref={homePathForRole(user?.role)}
        homeLabel={`CricketCompass — go to ${homeLabelForRole(user?.role)}`}
        endControls={
          <>
            <NotificationsBell compact surface={surface} />
            <AccessibilityMenu compact surface={surface} />
            <AccountMenu surface={surface} hasRibbon />
          </>
        }
        data-testid="settings-topbar"
      />
      {/* D221 O1 / #1121 supersedes D92 for the coach: Your players ·
          Grounds · Training & guides. Neither ribbon has a current item on
          Settings. */}
      {isCoach ? (coachRibbon ? <CoachRibbon /> : null) : <DestinationRow />}

      <div className="flex flex-col" style={{ gap: "var(--nw-space-stack)", padding: "16px 20px 20px" }}>
        <div className="flex flex-col" style={{ gap: 4 }}>
          <div className="nw-eyebrow">Settings</div>
          <div className="nw-title">Your account</div>
        </div>

        <SettingsDetailsCard user={user} club={playerRecord?.club} onEdit={() => setDetailsOpen(true)} />

        <SignOutButton ctl={signOut} />
        <SignOutError ctl={signOut} where="face" />

        <MoreSection screen="settings" data-testid="settings-more" initialOpen={openMore}>
          <div className="flex flex-col" style={{ gap: 10 }}>
            {isCoach && (
              <SettingsMoreCard
                icon={UserPlus}
                id="settings-add-player"
                title="Add a player"
                subtitle="Send a player an invite to link with you."
                data-testid="settings-add-player"
              >
                {/* F1 (privacy): the consent statement stays as body text, beside the action. */}
                <p data-testid="settings-add-player-consent" style={{ fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink-lede)", margin: 0 }}>
                  Invite a player by their account email. They'll see the invite in their Settings and choose whether to accept — you can't see any of their cricket data unless they do.
                </p>
                <UsageFirstTouch item="add_player">
                  <AddPlayerCard embedded kitType appearance="r5" />
                </UsageFirstTouch>
              </SettingsMoreCard>
            )}
            {isCoach && (
              <SettingsMoreCard
                icon={UserRound}
                title="Your coaching profile"
                subtitle="Your level and the photo players see."
                data-testid="settings-coach-profile"
              >
                <SecondaryAction onClick={() => setCoachProfileOpen(true)} data-testid="coach-profile-edit" style={OUTLINE_ACTION_STYLE}>
                  Edit your coaching profile
                </SecondaryAction>
              </SettingsMoreCard>
            )}
            <UsageFirstTouch item="passkeys">
              <PasskeysCard appearance="r5" />
            </UsageFirstTouch>
            {!isCoach &&
              (unpaid ? (
                gated(Users, "Your coach", "settings-gated-coach-access")
              ) : (
                <UsageFirstTouch item="coach_access">
                  <CoachAccessCardNew />
                </UsageFirstTouch>
              ))}
            {/* #1037 (D204.2): Billing is the first folded card after Coach access
                (a coach has no Coach access card, so it follows Passkeys). It
                exists only where the pay steps do (`newUi("pay")`); with that
                off Settings is what it was (D217). */}
            {newUi("pay") && <BillingCard appearance="r5" />}
            {/* #1571 (D417): Invite a friend, right after Billing, behind the same
                flag (it is a payments feature). New look only; the shipped
                Settings page never draws it. */}
            {newUi("pay") && (unpaid ? gated(Gift, "Invite a friend", "settings-gated-invite-friend") : <InviteFriendCard />)}
            {!isCoach && (
              <SectionCard
                icon={UserRound}
                title="Player Profile"
                usageItem="player_profile"
                collapsible
                defaultOpen={false}
                summary={playerRecord ? cricketProfileSummary({ club: playerRecord.club, team: playerRecord.school }) : SUMMARY_NONE}
                toggleTestid="settings-player-profile-toggle"
                testid="settings-player-profile-card"
                appearance="r5"
                tapBox
              >
                <PlayerProfile
                  showHeading={false}
                  frameless
                  kitType
                  hidePhoto
                  onSaved={(p) => setPlayerRecord({ club: p?.club, school: p?.school })}
                />
              </SectionCard>
            )}
            {!isCoach && (unpaid ? gated(HeartPulse, "Health and emergency contact", "settings-gated-health") : <HealthInfoCard appearance="r5" />)}
            {unpaid ? gated(Bell, "Notifications", "settings-gated-notifications") : <NotificationsCard appearance="r5" />}
            <SendFeedbackCard appearance="r5" />
            {/* #1514 (D407a): replay the guided tour, after Send feedback, both roles.
                `?replay=1` keeps the "seen" flag untouched. New look only (`tour`). */}
            {newUi("tour") && (
              <SettingsMoreCard
                icon={Compass}
                title="Take the tour"
                subtitle={isCoach ? "The four screens you saw after set-up." : "The seven screens you saw on your first day."}
                data-testid="settings-take-tour"
              >
                <SecondaryAction
                  as={Link}
                  to={tourReplayHref(isCoach ? "coach" : "player")}
                  data-testid="settings-take-tour-link"
                  style={OUTLINE_ACTION_STYLE}
                >
                  Start the tour
                </SecondaryAction>
              </SettingsMoreCard>
            )}
            {unpaid ? gated(BarChart3, "What we measure", "settings-gated-usage") : <UsageDisclosureCard appearance="r5" />}
            <SignOutEverywhere ctl={signOut} />
            <SignOutError ctl={signOut} where="everywhere" />
            {/* #1032: the way into deleting the account, last in More for a
                player and a coach alike (D204.2). Alert-outlined; deletes
                nothing itself. Hidden for the shared demo accounts. */}
            <DeleteAccountCard appearance="r5" />
          </div>
        </MoreSection>

        {/* Issue #1340 (D367.3): once the terms text exists, the two links
            share ONE row. Stacked, their 44px tap boxes (taller than the
            drawn line) overlapped and the second took the first's taps.
            Side by side with a column gap they cannot meet; a wrapped row
            keeps a row gap wider than the boxes reach. Hidden: the single
            Privacy link renders exactly as before. */}
        {(() => {
          const privacyLink = (
            <div data-testid="settings-privacy-link">
              <TextLink
                as={Link}
                to="/privacy"
                state={{ from: "/settings" }}
                className="underline"
                style={{ fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", "--nw-tap-lh": 1.5 }}
              >
                Privacy notice
              </TextLink>
            </div>
          );
          if (!termsReady()) return privacyLink;
          return (
            <div className="flex flex-wrap items-center" style={{ columnGap: 20, rowGap: 24 }} data-testid="settings-legal-links">
              {privacyLink}
              <div data-testid="settings-terms-link">
                <TextLink
                  as={Link}
                  to="/terms"
                  state={{ from: "/settings" }}
                  className="underline"
                  style={{ fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", "--nw-tap-lh": 1.5 }}
                >
                  Terms of use
                </TextLink>
              </div>
            </div>
          );
        })()}

        <p className="text-xs text-slate-500 px-1" data-testid="settings-about">
          CricketCompass <span data-testid="settings-build-stamp" className="font-mono">{BUILD_STAMP}</span>
          {" — quote this build if you report a problem."}
        </p>
      </div>

      <SettingsDetailsSheet open={detailsOpen} onOpenChange={setDetailsOpen} />
      {isCoach && (
        <SettingsSheet
          open={coachProfileOpen}
          onOpenChange={setCoachProfileOpen}
          title="Your coaching profile"
          data-testid="settings-coach-profile-sheet"
        >
          <CoachProfileCard frameless kitType />
        </SettingsSheet>
      )}
    </div>
  );
}
